import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { startOfMonth, endOfMonth } from 'date-fns';
import { formatInTz, DEFAULT_TIMEZONE } from '@/utils/timezone';
import { DEFAULT_COMPANY_LOCALE, type CompanyLocale } from '@/utils/locale';
import { toast } from 'sonner';

export interface DailySpendEntry {
  id?: string;
  campaign_id: string;
  spend_date: string;
  actual_cost: number;
  impressions?: number | null;
  clicks?: number | null;
  conversions?: number | null;
}

export interface CampaignWithSpend {
  id: string;
  name: string;
  is_active: boolean;
  lead_source_name?: string;
  dailySpend: Record<string, DailySpendEntry>;
}

export function useDailySpend(month: Date, timezone: string = DEFAULT_TIMEZONE) {
  const start = startOfMonth(month);
  const end = endOfMonth(month);

  return useQuery({
    queryKey: ['campaign-daily-spend', formatInTz(month, timezone, 'yyyy-MM'), timezone],
    queryFn: async () => {
      // Fetch all active campaigns
      const { data: campaigns, error: campaignsError } = await supabase
        .from('campaigns')
        .select(`
          id,
          name,
          is_active,
          lead_sources (name)
        `)
        .eq('is_active', true)
        .order('name');

      if (campaignsError) throw campaignsError;

      // Fetch daily spend for the month using timezone-aware formatting
      const { data: spend, error: spendError } = await supabase
        .from('campaign_daily_spend')
        .select('*')
        .gte('spend_date', formatInTz(start, timezone, 'yyyy-MM-dd'))
        .lte('spend_date', formatInTz(end, timezone, 'yyyy-MM-dd'));

      if (spendError) throw spendError;

      // Map spend data by campaign and date
      const spendByDate: Record<string, Record<string, DailySpendEntry>> = {};
      spend?.forEach(entry => {
        if (!spendByDate[entry.campaign_id]) {
          spendByDate[entry.campaign_id] = {};
        }
        spendByDate[entry.campaign_id][entry.spend_date] = {
          id: entry.id,
          campaign_id: entry.campaign_id,
          spend_date: entry.spend_date,
          actual_cost: entry.actual_cost,
          impressions: entry.impressions,
          clicks: entry.clicks,
          conversions: entry.conversions,
        };
      });

      // Return campaigns with their daily spend
      return campaigns?.map(c => ({
        id: c.id,
        name: c.name,
        is_active: c.is_active,
        lead_source_name: c.lead_sources?.name,
        dailySpend: spendByDate[c.id] || {},
      })) as CampaignWithSpend[];
    },
  });
}

export function useSaveDailySpend(locale: CompanyLocale = DEFAULT_COMPANY_LOCALE) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (entries: { campaignId: string; companyId: string; date: string; cost: number }[]) => {
      const records = entries.map(entry => ({
        campaign_id: entry.campaignId,
        company_id: entry.companyId,
        spend_date: entry.date,
        actual_cost: entry.cost,
        source_platform: 'manual',
        entered_by: user?.id,
        currency: locale.currency,
      }));

      const { error } = await supabase
        .from('campaign_daily_spend')
        .upsert(records, { onConflict: 'campaign_id,spend_date,source_platform' });

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Daily spend saved');
      queryClient.invalidateQueries({ queryKey: ['campaign-daily-spend'] });
      queryClient.invalidateQueries({ queryKey: ['reports', 'campaign-report'] });
    },
    onError: (error: Error) => {
      console.error('Daily spend save failed:', error);
      if (error.message.includes('violates check constraint')) {
        toast.error('Cannot save: Monthly total would be invalid. Please review your entries.');
      } else {
        toast.error('Failed to save: ' + error.message);
      }
    },
  });
}

export function useQuickSpendEntry(timezone: string = DEFAULT_TIMEZONE, locale: CompanyLocale = DEFAULT_COMPANY_LOCALE) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (entry: { 
      campaignId: string; 
      companyId: string; 
      date: Date; 
      cost: number;
      impressions?: number;
      clicks?: number;
      conversions?: number;
    }) => {
      const { error } = await supabase
        .from('campaign_daily_spend')
        .upsert({
          campaign_id: entry.campaignId,
          company_id: entry.companyId,
          spend_date: formatInTz(entry.date, timezone, 'yyyy-MM-dd'),
          actual_cost: entry.cost,
          impressions: entry.impressions || null,
          clicks: entry.clicks || null,
          conversions: entry.conversions || null,
          source_platform: 'manual',
          entered_by: user?.id,
          currency: locale.currency,
        }, { onConflict: 'campaign_id,spend_date,source_platform' });

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Spend entry saved');
      queryClient.invalidateQueries({ queryKey: ['campaign-daily-spend'] });
      queryClient.invalidateQueries({ queryKey: ['reports', 'campaign-report'] });
    },
    onError: (error: Error) => {
      toast.error('Failed to save: ' + error.message);
    },
  });
}
